import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider
} from '@chakra-ui/react';
import SEO from '../SEO';
import { generateSEOMetadata, staticPages } from '../../utils/seo';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Generate SEO metadata for privacy policy page
  const seoMetadata = generateSEOMetadata.staticPage(
    staticPages['privacy-policy'].name,
    staticPages['privacy-policy'].url,
    staticPages['privacy-policy'].description
  );

  return (
    <>
      <SEO {...seoMetadata} />
      <Box bg={useColorModeValue('gray.50', 'gray.900')} py={12}>
        <Container maxW="4xl" bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Heading as="h1" size="xl" mb={2} color="#4CAF50">
                Twygs Privacy Policy
              </Heading>
              <Text color="gray.500" fontSize="sm">
                <strong>Effective Date:</strong> {currentDate}
              </Text>
              <Text color="gray.500" fontSize="sm">
                <strong>Last Updated:</strong> {currentDate}
              </Text>
            </Box>

            <Box
              p={5}
              bg={useColorModeValue('green.50', 'green.900')}
              borderLeft="4px solid"
              borderColor="#4CAF50"
              borderRadius="md"
            >
              <Text>
                At Twygs, we are committed to protecting your privacy and ensuring that your
                personal information is handled in a safe, responsible, and transparent manner. This
                Privacy Policy explains how we collect, use, share, and protect your information
                when you use our web app, Twygs (the "Platform"). By using Twygs, you agree to the
                terms outlined in this policy.
              </Text>
            </Box>

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                1. Introduction
              </Heading>
              <Text color={textColor}>
                Twygs is a long-form content platform designed to empower independent journalists,
                content creators, and engaged readers by prioritizing quality, depth, and meaningful
                interactions. We believe in fostering a digital space where users can learn, grow,
                and connect without compromising their privacy. This Privacy Policy outlines how we
                handle your data to support these goals while ensuring your rights and control over
                your personal information.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                2. Information We Collect
              </Heading>
              <Text color={textColor} mb={4}>
                We collect the following types of information to provide and improve the Twygs
                experience:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.1 Account Information
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Username, first name, last name (optional), and email address (required for
                      registration and account management).
                    </ListItem>
                    <ListItem>
                      Optional profile information, such as bio or profile picture, if you choose to
                      provide it.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.2 Content Information
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Articles, comments, and other user-generated content you create or share on
                      the Platform.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.3 Activity Information
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Interactions on the Platform, including articles viewed, comments made, and
                      points awarded (up to 50 daily points, with 1-5 points per article or
                      comment).
                    </ListItem>
                    <ListItem>
                      Investment data, such as points invested in articles (10-100 points per
                      article) for ad revenue sharing.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.4 Bias Space Data
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Data derived from analyzing your content and activity across 8+ dimensions
                      (e.g., moral foundations, economic positions) to power features like
                      personalized recommendations and the anti-echo chamber toggle.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.5 Reputation Data
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Points received through the recognition system and your reputation tier (e.g.,
                      Newcomer to Luminary), which influences perks like featured posts.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    2.6 Device and Usage Data
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Information about your device, browser type, IP address, and usage patterns
                      (e.g., time spent on the Platform), collected via cookies and similar
                      technologies.
                    </ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                3. How We Use Your Information
              </Heading>
              <Text color={textColor} mb={4}>
                We use the information we collect for the following purposes:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.1 Platform Operations
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To manage your account, process your content, and facilitate interactions,
                      including the recognition and investment systems.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.2 Personalization
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To provide personalized content recommendations based on your activity and
                      bias space data.
                    </ListItem>
                    <ListItem>
                      To enable the anti-echo chamber toggle, which exposes you to diverse
                      perspectives.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.3 Ad Targeting
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To display relevant ads based on your activity and bias space data. This
                      supports our ad-based revenue model, which allows creators to earn and users
                      to benefit from investments in content.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.4 Analytics and Improvement
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To analyze usage patterns and improve the Platform's features, ensuring it
                      remains a space for growth and insight.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.5 Content Moderation
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To review user-generated content and ensure compliance with our community
                      guidelines, fostering a trusted environment.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.6 Legal Compliance
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      To comply with applicable laws, such as retaining data for dispute resolution
                      or meeting regulatory requirements.
                    </ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                4. Sharing Your Information
              </Heading>
              <Text color={textColor} mb={4}>
                We may share your information with the following parties:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.1 Third-Party Service Providers
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      We use third-party providers for hosting, analytics (e.g., tracking user
                      engagement), and ad networks to display targeted ads. These providers are
                      contractually obligated to protect your data and only use it for the purposes
                      we specify.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.2 Payment Processors
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      If applicable, we may share financial information with payment processors to
                      facilitate payouts from ad revenue shares. However, our current model uses an
                      internal points system.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.3 Legal Entities
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      We may disclose your information to law enforcement or government agencies if
                      required by law, such as to protect our rights, safety, or property, or to
                      comply with legal obligations.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Alert
                  status="info"
                  variant="left-accent"
                  borderLeftColor="#4CAF50"
                  bg={useColorModeValue('green.50', 'green.900')}
                  mt={4}
                >
                  <AlertIcon color="#4CAF50" />
                  <Text fontWeight="medium">
                    Important: We do not sell your personal information to third parties for
                    marketing purposes.
                  </Text>
                </Alert>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                5. Data Protection
              </Heading>
              <Text color={textColor} mb={4}>
                We take the security of your data seriously and implement the following measures to
                protect it:
              </Text>
              <UnorderedList spacing={2} pl={4}>
                <ListItem>
                  <strong>Encryption:</strong> Your data is encrypted both in transit and at rest.
                </ListItem>
                <ListItem>
                  <strong>Secure Servers:</strong> We use industry-standard security protocols to
                  store your data.
                </ListItem>
                <ListItem>
                  <strong>Internal Policies:</strong> We have strict procedures to prevent
                  unauthorized access, use, or disclosure of your information.
                </ListItem>
              </UnorderedList>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                6. Your Rights and Control Over Your Data
              </Heading>
              <Text color={textColor} mb={4}>
                You have the following rights regarding your personal information:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    6.1 Access and Review
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      You can request access to the data we hold about you to ensure its accuracy.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    6.2 Correction and Update
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      You can correct inaccuracies or update your information through your account
                      settings.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    6.3 Deletion
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      You can request the deletion of your data, subject to legal retention periods
                      (e.g., for resolving disputes).
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    6.4 Opt-Out Options
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      You can opt out of personalized recommendations, ad targeting, or profiling by
                      adjusting your account settings.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    6.5 Cookie Management
                  </Heading>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      You can manage your cookie preferences through your browser settings.
                    </ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                7. Cookies and Tracking Technologies
              </Heading>
              <Text color={textColor} mb={4}>
                We use cookies and similar technologies to:
              </Text>
              <UnorderedList spacing={2} pl={4} mb={4}>
                <ListItem>Authenticate users and maintain session integrity.</ListItem>
                <ListItem>Analyze usage patterns to improve the Platform.</ListItem>
                <ListItem>Deliver personalized ads based on your activity.</ListItem>
              </UnorderedList>
              <Text color={textColor}>
                You can control cookie settings through your browser, but disabling cookies may
                limit some Platform features.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                8. Automated Decision-Making
              </Heading>
              <Text color={textColor} mb={4}>
                Twygs uses AI to analyze content and activity for the bias space feature, which
                powers personalized recommendations and the anti-echo chamber toggle. This involves
                automated decision-making, including profiling. You have the right to:
              </Text>
              <UnorderedList spacing={2} pl={4}>
                <ListItem>Request information about how this process works.</ListItem>
                <ListItem>
                  Opt out of automated decision-making by adjusting your account settings.
                </ListItem>
              </UnorderedList>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                9. International Data Transfers
              </Heading>
              <Text color={textColor}>
                Your data may be transferred to and processed in countries outside your own,
                including the United States. We ensure that such transfers comply with applicable
                data protection laws, providing adequate safeguards for your information.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                10. Children's Privacy
              </Heading>
              <Text color={textColor}>
                Twygs is not intended for users under the age of 18. We do not knowingly collect
                personal information from children under 18. If you are a parent or guardian and
                believe your child has provided us with personal information, please contact us at{' '}
                <Link color="#4CAF50" href="mailto:hello@twygs.io">
                  hello@twygs.io
                </Link>
                , and we will take steps to delete such information. Twygs has designed this policy
                in order to comply with the Children’s Online Privacy Protection Act (“COPPA”).
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                11. Changes to This Privacy Policy
              </Heading>
              <Text color={textColor}>
                We may update this Privacy Policy from time to time to reflect changes in our
                practices or legal requirements. Any updates will be posted on this page, and the
                "Last Updated" date will be revised accordingly. We encourage you to review this
                policy periodically to stay informed about how we protect your data.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                12. Contact Us
              </Heading>
              <Text color={textColor} mb={2}>
                If you have any questions or concerns about this Privacy Policy or how your data is
                handled, please contact us at:
              </Text>
              <Text color={textColor}>
                Email:{' '}
                <Link color="#4CAF50" href="mailto:hello@twygs.io">
                  hello@twygs.io
                </Link>
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
